import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {CommonUtils} from '../utils/common';
import {Observable} from 'rxjs';
import {User} from '../models/user.model';
import {VetineoApiResponse} from '../models/vetineo-api-response.model';
import {map, take} from 'rxjs/operators';
import {UsersDgSearchResults} from "../models/datagrid-search-results/users.dg-search-results.model";
import {DataGridServiceInterface} from "./data-grid-service.interface";

/**
 * Serwis warstwy danych odpowiedzialny za zarządzanie użytkownikami aplikacji mobilnej.
 * Implementuje ustandaryzowany interfejs DataGrid, dostarczając metody do paginacji,
 * wyszukiwania i synchronizacji rekordów z backendem.
 */
@Injectable({
  providedIn: 'root'
})
export class AppUsersService implements DataGridServiceInterface<Observable<UsersDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną, podstawową listę użytkowników bez nakładania zaawansowanych filtrów.
   *
   * @param limit Maksymalna liczba rekordów do zwrócenia (rozmiar strony).
   * @param offset Przesunięcie wyników (od którego rekordu zacząć).
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny/pola, po którym następuje sortowanie.
   * @param showDeletedObjects Flaga określająca, czy dołączyć miękko usunięte (soft-deleted) rekordy.
   * @returns Observable emitujący obiekt wyników DataGrid specyficzny dla użytkowników.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UsersDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/app-users/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const usersResults = new UsersDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new User(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken, vetineoApiResponse.response.additionalCounter);
        return usersResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje zaawansowane wyszukiwanie użytkowników na podstawie przekazanych filtrów.
   * Parametry wyszukiwania są mapowane i wysyłane w ciele zapytania POST.
   *
   * @param limit Maksymalna liczba rekordów do zwrócenia.
   * @param offset Przesunięcie wyników.
   * @param searchFieldsData Mapa zawierająca klucze (nazwy kolumn) i wartości (szukane frazy).
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa pola do sortowania.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowany obiekt wyników DataGrid dla użytkowników.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UsersDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/app-users/search`, data).pipe(take(1), map(vetineoApiResponse => {
      console.log(vetineoApiResponse);
      if (vetineoApiResponse.ok) {
        return new UsersDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new User(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken, vetineoApiResponse.response.additionalCounter);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza indywidualną synchronizację profilu konkretnego użytkownika.
   *
   * @param uuid Globalnie unikalny identyfikator użytkownika do synchronizacji.
   * @returns Observable emitujący obiekt User.
   */
  syncAppUser(uuid: string): Observable<User> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/users/sync/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        console.log(vetineoApiResponse.response);
        return this.commonUtils.bestTypedCopyEver<User>(new User(), vetineoApiResponse.response);
      }
    }));
  }
}
